import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { Eye, Inbox } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { pcmpProposalCorrectionQueryOptions } from '../../api/pcmpMarketing.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpProposalCorrectionParams } from '../../types/pcmpMarketing.types';
import type { PcmpRequestedItem } from '../../types/pcmpRequested.types';
import { getPcmpRequestedColumns } from '../PcmpRequested/PcmpRequestedColumns';
import { PcmpRequestActButtons } from '../PcmpRequested/PcmpRequestActButtons';

interface PcmpProposalCorrectionListProps {
  params: PcmpProposalCorrectionParams;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

function renderRowActions(item: PcmpRequestedItem) {
  return (
    <>
      <PcmpRequestActButtons
        proposalId={item.proposalId}
        refNo={item.refNo}
        proposedAmount={item.proposedAmount}
        canAct={item.canAct}
        canProcess={item.canProcess}
        approveBlockedReason={item.approveBlockedReason}
        categoryCode={item.request.categoryCode}
        processOnly
        className="flex-nowrap"
      />
      <Link
        to="/pcmp/proposals/$proposalId"
        params={{ proposalId: item.proposalId }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
      >
        <Eye className="size-3.5" />
        View
      </Link>
    </>
  );
}

/**
 * The Proposals page's Proposal Correction tab (legacy LDS-813): every proposal
 * with an open Proposal Correction request. Rows only process; approving or
 * rejecting happens on the proposal, where the edits are.
 */
export function PcmpProposalCorrectionList({
  params,
  onPageChange,
  onLimitChange,
}: PcmpProposalCorrectionListProps) {
  const router = useRouter();
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpProposalCorrectionQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.meta.totalPages, onPageChange);

  const openProposal = useCallback(
    (item: PcmpRequestedItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const columns = useMemo(() => getPcmpRequestedColumns({ renderActions: renderRowActions }), []);

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load Proposal Correction"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      <DataTable
        data={data?.data ?? []}
        columns={columns}
        isLoading={isLoading}
        stickyFirstColumn
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage="No open Proposal Correction requests"
        skeletonRowCount={Math.min(params.limit, 15)}
        onRowClick={openProposal}
      />
      {data?.meta ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={onPageChange}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: onLimitChange }}
        />
      ) : null}
    </>
  );
}
